import { useContext, useCallback } from 'react';
import { Localization } from '../../../model/store/providers/Localization';
import './Section.scss';
import './MfaSection.scss';
import { useAuth0 } from '@auth0/auth0-react';
import { auth0Domain, mfaScopes, rawTenantDomain, redirectUri } from '../../../config';
import axios from 'axios';
import { MfaFactors } from '../../../model/store/providers/MfaFactors';

export type Props = {
	title: string;
}

export const MfaSection = () => {
	const { translations } = useContext(Localization.State);
	const {
		mainPage: {
			mfaSection: {
				title,
			}
		}
	} = translations;

	return <>
		<h1 className='section__title'>{title}</h1>
		<div className='section__body'>
			<PhoneFactor />
		</div>
	</>;
}

function PhoneFactor() {
	const dispatchMfaFactors = useContext(MfaFactors.Dispatch);
	const { translations } = useContext(Localization.State);
	const { getAccessTokenSilently, logout } = useAuth0();
	const { phoneFactor, biometricsFactor, recoveryCodeFactor } = useContext(MfaFactors.State);

	const {
		mainPage: {
			mfaSection: {
				phone: {
					phoneLabel,
					phoneChangeBtn,
					phoneRemoveBtn
				},
				biometrics: {
					biometricsLabel,
					biometricsRemoveBtn
				},
				recoveryCodes: {
					recoveryCodesLabel,
					recoveryCodesRemoveBtn
				}
			}
		}
	} = translations;

	const removeMfaFactor = useCallback((mfaId:string) => void async function() {

		const token = await getAccessTokenSilently({
			scope: mfaScopes,
			audience: `https://${rawTenantDomain}/mfa/`
		});

		await axios.delete(`https://${auth0Domain}/mfa/authenticators/${mfaId}`, {
			headers: {
				authorization: `Bearer ${token}`
			}
		});

	}() , [getAccessTokenSilently]);

	const removePhoneFactor = useCallback(() => {
		removeMfaFactor(phoneFactor!.id)
		dispatchMfaFactors({type: 'removeMfaFactor', factor: 'phone'})
	}, [phoneFactor, removeMfaFactor, dispatchMfaFactors]);
	const removeBiometricsFactor = useCallback(() => {
		removeMfaFactor(biometricsFactor!.id)
		dispatchMfaFactors({type: 'removeMfaFactor', factor: 'biometrics'})
	}, [biometricsFactor, removeMfaFactor, dispatchMfaFactors]);
	const removeRecoveryCodeFactor = useCallback(() => {
		removeMfaFactor(recoveryCodeFactor!.id)
		dispatchMfaFactors({type: 'removeMfaFactor', factor: 'recoveryCode'})
	}, [recoveryCodeFactor, removeMfaFactor, dispatchMfaFactors]);

	const changePhoneNumber = useCallback(() => void async function() {

		if (!phoneFactor) return;

		const token = await getAccessTokenSilently({
			scope: mfaScopes,
			audience: `https://${rawTenantDomain}/mfa/`
		});

		await axios.delete(`https://${auth0Domain}/mfa/authenticators/${phoneFactor.id}`, {
			headers: {
				authorization: `Bearer ${token}`
			}
		});

		const withoutTrailingSlash = redirectUri.replace(/\/+$/, '');

		logout({ returnTo: `${withoutTrailingSlash}/login` });
	}(), [getAccessTokenSilently, phoneFactor, logout]);

	const phoneFactorSection = (!phoneFactor || !phoneFactor.value) ? null : (
		<div className='section__item'>
			<div className='mfa-section__left'>
				<div className=''>
					<span>{phoneLabel}</span> <span className='mfa-section__phone'>
						{phoneFactor.value}
					</span>
				</div>
			</div>
			<span className='wedge' />
			<div className='mfa-section__right'>
				<span className='section__secondary-link' onClick={changePhoneNumber}>{phoneChangeBtn}</span>
				<span className='section__secondary-link' onClick={removePhoneFactor}>{phoneRemoveBtn}</span>
			</div>
		</div>
	);

	const biometricsFactorSection = (!biometricsFactor || !biometricsFactor.value) ? null : (
		<div className='section__item'>
			<div className='mfa-section__left'>
				<div className=''>
					<span>{biometricsLabel}</span> <span className='mfa-section__phone'>
						{biometricsFactor.value}
					</span>
				</div>
			</div>
			<span className='wedge' />
			<div className='mfa-section__right'>
				<span className='section__secondary-link' onClick={removeBiometricsFactor}>{biometricsRemoveBtn}</span>
			</div>
		</div>
	);

	const recoveryCodeFactorSection = (!recoveryCodeFactor) ? null : (
		<div className='section__item'>
			<div className='mfa-section__left'>
				<div className=''>
					<span>{recoveryCodesLabel}</span> <span className='mfa-section__phone'>
						{recoveryCodeFactor.value}
					</span>
				</div>
			</div>
			<span className='wedge' />
			<div className='mfa-section__right'>
				<span className='section__secondary-link' onClick={removeRecoveryCodeFactor}>{recoveryCodesRemoveBtn}</span>
			</div>
		</div>
	);

	return (
		<div className='mfa'>
			{phoneFactorSection}
			{biometricsFactorSection}
			{recoveryCodeFactorSection}
		</div>
	);
}